import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useParams, useLocation } from "wouter"
import { ArrowRight, ArrowLeft, Briefcase, Calendar, Target, Building, Plus, X } from "lucide-react"
import { useValueAssessment, SaveSummarySectionOptions } from "@/contexts/ValueAssessmentContext"
import { useState, useEffect, useCallback } from "react"
import { useToast } from "@/hooks/use-toast"
import { VALID_REVENUE_BANDS } from "@/constants/revenueBands"
import {erpOptions, goalOptions} from "@/constants/kickoffConstants.ts";
import { useUnsavedChangesWarning } from "@/hooks/useUnsavedChangesWarning"


export default function ProjectKickoffScreen() {
  const params = useParams()
  const [_location, navigate] = useLocation()
  
  // Get projectId from URL params (traditional approach)
  const projectId = params.projectId
  // Public token awareness: treat the presence of token as public flow (no auth-backed saves)
  const qs = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
  const publicToken = (qs?.get('t') || (typeof window !== 'undefined' ? sessionStorage.getItem('public_token') || '' : '')) as string
  const isPublic = Boolean(qs?.get('public') === 'true' || publicToken)
  const { data, updateSection, isLoading, isSaving, saveSummarySection, project } = useValueAssessment()
  const { toast } = useToast()
  
  // FIXED: Start with empty state - let hydration handle loading (like Strategic Context)
  const [formData, setFormData] = useState({
    clientName: "",
    erpSystems: [] as string[],
    legalEntities: 0,
    revenueBand: "",
    currentCloseDays: 0,
    targetCloseDays: 0,
    strategicGoals: [] as string[]
  })

  const [customGoal, setCustomGoal] = useState("")
  const [sliderValue, setSliderValue] = useState([formData.currentCloseDays])
  const [hasLocalChanges, setHasLocalChanges] = useState(false)

  // FIXED: Proper hydration that reacts to kickoff data changes specifically (like other screens)
  useEffect(() => {
    // Always hydrate data, even if empty to ensure form is properly initialized (like Strategic Context)
    if (data.kickoff !== undefined) {
      const hydratedData = {
        clientName: data.kickoff.clientName || project?.client_org || "",
        erpSystems: Array.isArray(data.kickoff.erpSystems) ? data.kickoff.erpSystems : [],
        legalEntities: data.kickoff.legalEntities || 0,
        revenueBand: data.kickoff.revenueBand || "",
        currentCloseDays: data.kickoff.currentCloseDays || 0,
        targetCloseDays: data.kickoff.targetCloseDays || 0,
        strategicGoals: Array.isArray(data.kickoff.strategicGoals) ? data.kickoff.strategicGoals : []
      }
      
      setFormData(hydratedData)
      setSliderValue([hydratedData.currentCloseDays])
      setHasLocalChanges(false) // Reset local changes flag after hydration
    }
    
  }, [data.kickoff]) // CRITICAL FIX: React to kickoff changes specifically, not all data

  const constructKickoffData = (formData: any) => ({
    clientName: formData.clientName,
    erpSystems: formData.erpSystems,
    revenueBand: formData.revenueBand,
    legalEntities: formData.legalEntities,
    currentCloseDays: formData.currentCloseDays,
    targetCloseDays: formData.targetCloseDays,
    strategicGoals: formData.strategicGoals
  })

  const persistKickoff = useCallback(async (kickoffData: ReturnType<typeof constructKickoffData>, options?: SaveSummarySectionOptions) => {
    const kickoffPayload = {
      clientName: kickoffData.clientName || '',
      erpSystems: kickoffData.erpSystems || [],
      revenueBand: kickoffData.revenueBand || '',
      legalEntities: kickoffData.legalEntities || 0,
      currentCloseDays: kickoffData.currentCloseDays || 0,
      targetCloseDays: kickoffData.targetCloseDays || 0,
      strategicGoals: kickoffData.strategicGoals || []
    };

    await saveSummarySection({
      kickoff: kickoffPayload,
      valueSummary: {
        kickoff: kickoffPayload
      },
      lastUpdatedAt: new Date().toISOString(),
      schemaVersion: 'v2.1'
    }, options);
    updateSection('kickoff', kickoffPayload, { autoSave: false });
    setHasLocalChanges(false);
  }, [saveSummarySection, updateSection]);

  const persistKickoffWithCurrent = useCallback((options?: SaveSummarySectionOptions) => {
    const kickoffData = constructKickoffData(formData);
    return persistKickoff(kickoffData, options);
  }, [formData, persistKickoff]);

  const handleContinue = async () => {
    if (!formData.clientName || !formData.revenueBand || formData.legalEntities === 0 || formData.erpSystems.length === 0) {
      toast({
        title: "Missing Information",
        description: "Please complete all required fields before continuing.",
        variant: "destructive"
      })
      return
    }

    console.log('🚀 [SAVE] Starting Project Kickoff final save operation')
    
    try {
      // CRITICAL: Use immediate save to ensure data persists before navigation
      console.log('💾 [SAVE] Persisting kickoff data before navigation...');
      if (hasLocalChanges) {
        await persistKickoffWithCurrent();
      }
      console.log('✅ [SAVE] Project Kickoff save successful - proceeding to navigation');
      
      // Preserve public parameter if present
      const urlParams = new URLSearchParams(window.location.search)
      const isPublic = urlParams.get('public') === 'true' || urlParams.has('t')
      const t = urlParams.get('t')
      const nextUrl = isPublic 
        ? `/value-assessment/${projectId}/context?public=true${t ? `&t=${t}` : ''}`
        : `/value-assessment/${projectId}/context`
        
        navigate(nextUrl);
    } catch (error) {
      console.error('❌ [SAVE] Failed to save Project Kickoff data:', error);
      toast({
        title: "Save Error",
        description: "Failed to save project kickoff data. Please try again.",
        variant: "destructive",
      });
    }
  }

  const handleBack = async () => {
    if (hasLocalChanges) {
      try {
        await persistKickoffWithCurrent()
      } catch (error) {
        console.error('❌ [SAVE] Failed to save Project Kickoff data before exiting:', error)
        toast({
          title: "Save Error",
          description: "Failed to save project kickoff data. Please try again before leaving.",
          variant: "destructive",
        });
        return
      }
    }

    navigate(`/projects`)
  }

  const handleInputChange = (field: string, value: any) => {
    //TODO there is an issue here, when I select revenue Band, I can see [INPUT CHANGE] Project Kickoff field revenueBand changed: Emerging ProjectKickoffScreen.tsx:228:13 logged but it is not sent in the request
    console.log(`🔄 [INPUT CHANGE] Project Kickoff field ${field} changed:`, value);
    
    // FIXED: Use Strategic Context pattern - set local changes flag, don't call updateSection immediately
    setHasLocalChanges(true);
    const updatedFormData = { ...formData, [field]: value };
    setFormData(updatedFormData);
  }

  useUnsavedChangesWarning(hasLocalChanges, (options) => {
    persistKickoffWithCurrent(options).catch(() => {})
  })

  const addCustomGoal = () => {
    if (customGoal.trim() && !formData.strategicGoals.includes(customGoal.trim())) {
      console.log(`🔄 [GOAL ADD] Adding strategic goal:`, customGoal.trim());
      
      setHasLocalChanges(true);
      const updatedFormData = {
        ...formData,
        strategicGoals: [...formData.strategicGoals, customGoal.trim()]
      };
      
      setFormData(updatedFormData);
      setCustomGoal("");
    }
  }

  const removeGoal = (goalToRemove: string) => {
    console.log(`🔄 [GOAL REMOVE] Removing strategic goal:`, goalToRemove);
    
    setHasLocalChanges(true);
    const updatedFormData = {
      ...formData,
      strategicGoals: formData.strategicGoals.filter(goal => goal !== goalToRemove)
    };
    
    setFormData(updatedFormData);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4AE2C9] mx-auto"></div>
          <p className="text-gray-400 mt-2">Loading project details...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Project Kickoff</h1>
          <p className="text-gray-400 mt-1">Let's gather some basic information about your organization and project goals</p>
        </div>
      </div>

      {/* Client Name */}
      <Card className="bg-forestBlack/70 border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-[#4AE2C9]">
            <Briefcase className="w-5 h-5" />
            Client Organization
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="clientName" className="text-gray-300">Client Organization Name</Label>
              <Input
                id="clientName"
                value={formData.clientName}
                onChange={(e) => handleInputChange('clientName', e.target.value)}
                placeholder="Enter client organization name"
                className="bg-[#0F172A] border-[#334155] text-white mt-2"
                data-cy="clientName"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Size & Revenue */}
      <Card className="bg-forestBlack/70 border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-[#F59E0B]">
            <Building className="w-5 h-5" />
            Organization Scale
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="text-gray-300">Annual Revenue Band</Label>
              <Select value={formData.revenueBand} onValueChange={(value) => handleInputChange('revenueBand', value)}>
                <SelectTrigger data-cy="revenue-band-select" className="bg-[#0F172A] border-[#334155] text-white mt-2">
                  <SelectValue placeholder="Select revenue band" />
                </SelectTrigger>
                <SelectContent className="bg-forestBlack border-[#334155]">
                  {VALID_REVENUE_BANDS.map((band) => (
                      <SelectItem
                          key={band}
                          value={band}
                          data-cy={`revenue-band-option-${band.toLowerCase().replace(/\s+/g, '-')}`}
                          className="text-white hover:bg-[#334155]"
                      >
                        {band === 'Emerging' ? 'Emerging ($0 – $5B)' :
                            band === 'Enterprise' ? 'Enterprise ($5B – $25B)' :
                                'Large Enterprise ($25B+)'}
                      </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="legalEntities" className="text-gray-300">Number of Legal Entities</Label>
              <Input
                id="legalEntities"
                type="number"
                value={formData.legalEntities === 0 ? "" : formData.legalEntities}
                onChange={(e) => handleInputChange('legalEntities', parseInt(e.target.value) || 0)}
                onFocus={(e) => {
                  if (e.target.value === "0") {
                    e.target.value = ""
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === "") {
                    handleInputChange('legalEntities', 0)
                  }
                }}
                placeholder="Enter number of entities"
                className="bg-[#0F172A] border-[#334155] text-white mt-2"
                data-cy="numberOfEntities"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current State */}
      <Card className="bg-forestBlack/70 border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-[#EC4899]">
            <Calendar className="w-5 h-5" />
            Current Close Process
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-gray-300">Primary ERP Systems</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
              {erpOptions.map((erp) => (
                <div key={erp} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={erp}
                    data-cy={`erp-${erp.toLowerCase().replace(/\s+/g, '-')}`}
                    checked={formData.erpSystems.includes(erp)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleInputChange('erpSystems', [...formData.erpSystems, erp])
                      } else {
                        handleInputChange('erpSystems', formData.erpSystems.filter(system => system !== erp))
                      }
                    }}
                    className="w-4 h-4 accent-[#4AE2C9] bg-[#0F172A] border-[#334155] rounded focus:ring-primary focus:ring-2 checked:bg-primary checked:border-primary"
                  />
                  <Label htmlFor={erp} className="text-gray-300 text-sm">{erp}</Label>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="currentCloseDays" className="text-gray-300">Current Close Days</Label>
              <Input
                id="currentCloseDays"
                type="number"
                min={1}
                max={30}
                value={formData.currentCloseDays === 0 ? "" : formData.currentCloseDays}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 0
                  handleInputChange('currentCloseDays', value)
                  setSliderValue([value])
                }}
                onFocus={(e) => {
                  if (e.target.value === "0") {
                    e.target.value = ""
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === "") {
                    handleInputChange('currentCloseDays', 0)
                    setSliderValue([0])
                  }
                }}
                placeholder="Enter current close days"
                className="bg-[#0F172A] border-[#334155] text-white mt-2"
              />
            </div>
            
            <div>
              <Label htmlFor="targetCloseDays" className="text-gray-300">Target Close Days</Label>
              <Input
                id="targetCloseDays"
                type="number"
                min={1}
                max={30}
                value={formData.targetCloseDays === 0 ? "" : formData.targetCloseDays}
                onChange={(e) => handleInputChange('targetCloseDays', parseInt(e.target.value) || 0)}
                onFocus={(e) => {
                  if (e.target.value === "0") {
                    e.target.value = ""
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === "") {
                    handleInputChange('targetCloseDays', 0)
                  }
                }}
                placeholder="Enter target days"
                className="bg-[#0F172A] border-[#334155] text-white mt-2"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Strategic Goals */}
      <Card className="bg-forestBlack/70 border-[#334155]">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-[#4AE2C9]">
            <Target className="w-5 h-5" />
            Strategic Goals
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-gray-300">What are your key strategic goals for this initiative?</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
              {goalOptions.map((goal) => (
                <div key={goal} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={goal}
                    checked={formData.strategicGoals.includes(goal)}
                    data-cy={`goal-${goal.toLowerCase().replace(/\s+/g, '-')}`}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleInputChange('strategicGoals', [...formData.strategicGoals, goal])
                      } else {
                        handleInputChange('strategicGoals', formData.strategicGoals.filter(g => g !== goal))
                      }
                    }}
                    className="w-4 h-4 accent-[#4AE2C9] bg-[#0F172A] border-[#334155] rounded focus:ring-[#4AE2C9] focus:ring-2 checked:bg-[#4AE2C9] checked:border-[#4AE2C9]"
                  />
                  <Label htmlFor={goal} className="text-gray-300 text-sm">{goal}</Label>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Goal Input */}
          <div className="flex gap-2">
            <Input
              value={customGoal}
              onChange={(e) => setCustomGoal(e.target.value)}
              placeholder="Add custom strategic goal..."
              className="bg-[#0F172A] border-[#334155] text-white"
              onKeyPress={(e) => e.key === 'Enter' && addCustomGoal()}
            />
            <Button
              onClick={addCustomGoal}
              size="sm"
              className="bg-[#4AE2C9] hover:bg-[#3DD5B9] text-[#0F172A] font-medium"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {/* Selected Goals */}
          {formData.strategicGoals.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.strategicGoals.map((goal) => (
                <span
                  key={goal}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-[#4AE2C9]/20 border border-primaryHover rounded-md text-primary text-sm"
                >
                  {goal}
                  <button
                    onClick={() => removeGoal(goal)}
                    className="text-primary hover:text-white"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button 
          onClick={handleBack}
          variant="outline"
          className=""
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </Button>
        <Button 
          onClick={handleContinue}
          disabled={isSaving}
          className="bg-primary text-forestBlack hover:bg-primaryHover"
          data-cy="toStrategicContext"
        >
          {isSaving ? "Saving..." : "Continue to Strategic Context"}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  )
}
